import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

export interface Batch {
  id: number;
  /** 技术名/短代号，唯一。例 "2026H1"。仅用于内部标识、PDF 文件名、日志等。 */
  name: string;
  /** UI 展示名（业务友好），可空。例 "2026年上半年晋升提升"。前端各下拉、Banner 一律走 batchLabel(b)。 */
  displayName?: string | null;
  phase: string;
  startAt: string;
  endAt: string;
}

/**
 * 渲染批次时统一用这个：业务展示名优先，回退到技术 name。
 *
 * @example
 *   options={batches.map((b) => ({ label: batchLabel(b), value: b.id }))}
 */
export function batchLabel(b: Pick<Batch, 'name' | 'displayName'> | null | undefined): string {
  if (!b) return '';
  return (b.displayName && b.displayName.trim()) || b.name;
}

/**
 * 拉所有批次，并自动选中"最新一个 ONGOING（或最近创建的那个）"作为默认 batchId。
 */
export function useBatches() {
  const [searchParams] = useSearchParams();
  // PR-7：支持从 URL ?batchId= 进入并选中（总览看板「九宫格 · 查看详情 →」跳转九宫格页）。
  const initialBatchId = (() => {
    const raw = searchParams.get('batchId');
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : undefined;
  })();
  const [batchId, setBatchId] = useState<number | undefined>(initialBatchId);

  const { data: batches = [], isLoading } = useQuery<Batch[]>({
    queryKey: ['batches'],
    queryFn: () => api.get('/admin/batches'),
  });

  useEffect(() => {
    if (batchId !== undefined) return;
    if (batches.length === 0) return;
    const ongoing = batches.find((b) => b.phase === 'ONGOING');
    setBatchId((ongoing ?? batches[0]).id);
  }, [batches, batchId]);

  return { batches, batchId, setBatchId, isLoading };
}
